/* eslint-disable react/forbid-prop-types */
import React, {
  useEffect, useMemo, useState,
} from 'react';
import { useRecoilValue, useRecoilState } from 'recoil';
import PropTypes from 'prop-types';
import ClientSideConfigSelector from '../../../../recoil/cashout/ClientSideConfigSelector';
import CashoutOnboardingStatusAtom, { CashoutOnboardingStatuses } from '../../../../recoil/cashout/CashoutOnboardingStatusAtom';
import TaxAccountAtom from '../../../../recoil/cashout/TaxAccountAtom';
import Loading from '../../../general/Loading';
import CashoutConfirmationModal from './confirmation/CashoutConfirmationModal';
import CashoutEmailModalContent from './CashoutEmailModalContent';
import {
  ClaimRewardKycStatus,
  RegistrationStatus,
} from '../../../../../client/claim-reward/constants';
import CashoutOnboardingModalContent from './CashoutOnboardingModalContent';
import CashoutKycModalContent from './CashoutKycModalContent';
import CashoutOnboardingProcessingInfoModal from './CashoutOnboardingProcessingInfoModal';

export default function CashoutModal({
  kycAccount, payAccount, selectedPackage, onRefresh, onClose,
}) {
  const config = useRecoilValue(ClientSideConfigSelector);
  const taxAccount = useRecoilValue(TaxAccountAtom);

  const {
    live: {
      claimReward: {
        paymentProvider,
        onboardingProcessingInfoEnabled = true,
        providers: {
          [paymentProvider]: {
            requiresEmail = false,
            requiresRegistration = false,
          },
        },
      } = {},
    } = {},
  } = config || {};

  const [email, setEmail] = useState(null);
  const [countryCode, setCountryCode] = useState(null);

  const [onboardingStatus, setOnboardingStatus] = useRecoilState(CashoutOnboardingStatusAtom);

  const isOnboardingInProcess = () => {
    const { status } = payAccount;

    if (status === RegistrationStatus.Unregistered) {
      return false;
    }

    if (status === RegistrationStatus.RegisteredVerified) {
      return false;
    }

    return onboardingStatus === CashoutOnboardingStatuses.submitted;
  };

  const isOnboardingRequires = () => {
    const {
      status: payAccountStatus,
    } = payAccount;
    return requiresRegistration && payAccountStatus !== RegistrationStatus.RegisteredVerified;
  };

  const isEmailRequires = () => requiresEmail && !email;

  const isKycVerificationRequires = () => {
    const {
      provider: kycProvider,
      status: kycProviderStatus,
    } = kycAccount;

    if (!kycProvider || kycProvider === 'none') {
      return false;
    }

    return kycProviderStatus === ClaimRewardKycStatus.NotApproved;
  };

  const content = useMemo(() => {
    if (!payAccount || !kycAccount || !taxAccount) {
      return <Loading />;
    }

    if (isKycVerificationRequires()) {
      return (
        <CashoutKycModalContent
          onContinue={onRefresh}
          onClose={onClose}
        />
      );
    }

    if (onboardingProcessingInfoEnabled && isOnboardingInProcess()) {
      return (
        <CashoutOnboardingProcessingInfoModal
          onClose={async () => {
            setOnboardingStatus(CashoutOnboardingStatuses.idle);
            await onRefresh();
            onClose();
          }}
        />
      );
    }

    if (isOnboardingRequires()) {
      return (
        <CashoutOnboardingModalContent
          payAccount={payAccount}
          onPaymentMethodUpdate={async () => {
            setOnboardingStatus(CashoutOnboardingStatuses.submitted);
            await onRefresh();
          }}
          onClose={onClose}
        />
      );
    }

    if (isEmailRequires()) {
      return (
        <CashoutEmailModalContent
          onSubmit={(newEmail, newCountryCode) => {
            setEmail(newEmail);
            setCountryCode(newCountryCode);
          }}
          onClose={onClose}
        />
      );
    }

    return (
      <CashoutConfirmationModal
        payAccount={{
          ...payAccount,
          ...(email && { email }),
          ...(countryCode && { countryCode }),
        }}
        selectedPackage={selectedPackage}
        onEmailChanged={(newEmail) => {
          setEmail(newEmail);
        }}
        onCountryCodeChanged={(newCountryCode) => {
          setCountryCode(newCountryCode);
        }}
        onContinue={() => {
          onRefresh();
          onClose();
        }}
        onPaymentMethodUpdate={async () => {
          setOnboardingStatus(CashoutOnboardingStatuses.submitted);
          await onRefresh();
        }}
        onClose={onClose}
      />
    );
    // eslint-disable-next-line
  }, [selectedPackage, taxAccount, kycAccount, payAccount, email, countryCode, onboardingStatus]);

  useEffect(() => {
    if (!email && payAccount.providerDisplay) {
      setEmail(payAccount.providerDisplay);
    }
    if (!countryCode && payAccount.countryCode) {
      setCountryCode(payAccount.countryCode);
    }
    // eslint-disable-next-line
  }, [payAccount]);

  return (
    <div>
      { content }
    </div>
  );
}

CashoutModal.propTypes = {
  payAccount: PropTypes.shape({
    provider: PropTypes.string.isRequired,
    status: PropTypes.string,
    providerDisplay: PropTypes.string, // could be undefined
    countryCode: PropTypes.string, // could be undefined
  }).isRequired,
  kycAccount: PropTypes.shape({
    provider: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
  }).isRequired,
  selectedPackage: PropTypes.shape({
    productId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  }).isRequired,
  onRefresh: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};
